import { useEffect, useState } from "react";
import type { AgentEvent } from "../../../lib/types";
import { cn } from "../../../lib/cn";
import { StatusBadge } from "../../shared/StatusBadge";
import { CopyButton, TargetText } from "./CopyControls";
import { Collapse, Disclosure, IconChip, MonoBlock } from "./primitives";
import { deriveToolStatus, toolMeta, toolTarget } from "./toolMeta";

/**
 * A tool call paired with its result (when it has arrived) as one card —
 * Codex/Claude-Code style. Header: action glyph, verb, target (path or
 * command, truncated but copyable), and a live status badge. Details (input +
 * result) are collapsed by default and reveal with a smooth height animation;
 * an error opens itself and hairlines the card red so it never hides.
 */
export function ToolCallCard({
  event,
  result,
}: {
  event: AgentEvent;
  result?: AgentEvent;
}) {
  const name = String(event.payload.name ?? "tool");
  const input = event.payload.input as Record<string, unknown> | undefined;
  const meta = toolMeta(name);
  const Icon = meta.Icon;
  const target = toolTarget(name, input);
  const targetKind =
    meta.category === "read" || meta.category === "write" ? "path" : "text";

  const hasResult = Boolean(result);
  const isError = Boolean(result?.payload.is_error);
  const status = deriveToolStatus(hasResult, isError);
  const resultContent = String(result?.payload.content ?? "");
  const hasInput = Boolean(input && Object.keys(input).length > 0);
  const hasDetails = hasInput || hasResult;

  const [open, setOpen] = useState(false);
  const [opened, setOpened] = useState(false); // lazy-mount: render details once

  // Errors reveal themselves — the operator shouldn't have to dig for them.
  useEffect(() => {
    if (isError) {
      setOpen(true);
      setOpened(true);
    }
  }, [isError]);

  const toggle = () => {
    setOpen((v) => {
      const next = !v;
      if (next) setOpened(true);
      return next;
    });
  };

  return (
    <div
      className="group rounded-md border border-border-subtle bg-bg-surface transition-colors duration-fast ease-out hover:border-border-default"
      style={isError ? { borderColor: "var(--status-error)" } : undefined}
    >
      <div className="flex items-center pr-1.5">
        <button
          type="button"
          aria-expanded={hasDetails ? open : undefined}
          onClick={hasDetails ? toggle : undefined}
          disabled={!hasDetails}
          className={cn(
            "flex-1 min-w-0 flex items-center gap-2 px-2.5 py-1.5 text-left rounded-md",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent",
            !hasDetails && "cursor-default",
          )}
        >
          {hasDetails ? (
            <Disclosure open={open} />
          ) : (
            <span className="w-3.5 shrink-0" aria-hidden="true" />
          )}
          <IconChip>
            <Icon size={13} />
          </IconChip>
          <span className="text-caption text-text-tertiary uppercase tracking-wide shrink-0">
            {meta.label}
          </span>
          {target ? (
            <TargetText value={target} kind={targetKind} className="flex-1" />
          ) : (
            <span className="flex-1" />
          )}
          <StatusBadge key={status} status={status} />
        </button>
        {target && (
          <CopyButton
            value={target}
            label={targetKind === "path" ? "Copy path" : "Copy"}
            className="self-center opacity-0 group-hover:opacity-100 focus-visible:opacity-100"
          />
        )}
      </div>

      {hasDetails && (
        <Collapse open={open}>
          {(open || opened) && (
            <div className="px-2.5 pb-2.5 pt-0.5 space-y-2">
              {hasInput && <MonoBlock label="input">{safeStringify(input)}</MonoBlock>}
              {hasResult && (
                <MonoBlock
                  label={isError ? "error" : "result"}
                  tone={isError ? "error" : "default"}
                >
                  {resultContent || "(empty)"}
                </MonoBlock>
              )}
            </div>
          )}
        </Collapse>
      )}
    </div>
  );
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    return String(value);
  }
}
